// samlla#cb22: drafts are scoped per user so a second account in the same tab
// never sees the first one's tracker name/description. The pre-cb22 unscoped
// key is removed on sight and never read.
const STORAGE_PREFIX = 'tracker_new_wizard_draft_v2';

function storageKey(userId: string): string {
	return `${STORAGE_PREFIX}:${userId}`;
}

export interface WizardDraft {
	name?: string;
	description?: string;
	is_journal_mode?: boolean;
	category_id?: string | null;
	// SAM-698: template provenance — kept so a reload doesn't silently drop
	// source_template_id / color / icon before submit.
	source_template_id?: string;
	source_template_locale?: 'pl' | 'en';
	template_color?: string;
	template_icon?: string;
	metrics?: Array<{
		name: string;
		metric_type: 'number' | 'duration' | 'scale' | 'select';
		unit?: string;
		tracking_mode: 'accumulate' | 'odometer' | 'whittle_down';
		description?: string;
		scale_start?: number;
		scale_end?: number;
		scale_step?: number;
		select_options?: Array<{ id?: string | null; label: string }>;
	}>;
}

export function loadDraft(userId: string): WizardDraft | null {
	if (typeof window === 'undefined') return null;

	try {
		sessionStorage.removeItem(STORAGE_PREFIX);
		const stored = sessionStorage.getItem(storageKey(userId));
		if (!stored) return null;
		return JSON.parse(stored) as WizardDraft;
	} catch (error) {
		console.error('Failed to load wizard draft:', error);
		return null;
	}
}

export function saveDraft(userId: string, draft: WizardDraft): void {
	if (typeof window === 'undefined') return;

	try {
		sessionStorage.setItem(storageKey(userId), JSON.stringify(draft));
	} catch (error) {
		console.error('Failed to save wizard draft:', error);
	}
}

export function clearDraft(userId: string): void {
	if (typeof window === 'undefined') return;

	try {
		sessionStorage.removeItem(storageKey(userId));
	} catch (error) {
		console.error('Failed to clear wizard draft:', error);
	}
}

/** Removes every user's draft (and the legacy unscoped one) — called on sign-out. */
export function clearAllDrafts(): void {
	if (typeof window === 'undefined') return;

	try {
		const keys: string[] = [];
		for (let i = 0; i < sessionStorage.length; i++) {
			const key = sessionStorage.key(i);
			if (key === STORAGE_PREFIX || key?.startsWith(`${STORAGE_PREFIX}:`)) keys.push(key);
		}
		for (const key of keys) sessionStorage.removeItem(key);
	} catch {
		// Best effort on sign-out: storage may be unavailable, and every draft
		// is keyed per user anyway, so nothing leaks into another account.
	}
}

/** Keeps the current user's reload-safe draft while removing legacy and other tenants. */
export function clearOtherUsersDrafts(userId: string): void {
	if (typeof window === 'undefined') return;

	try {
		const currentKey = storageKey(userId);
		const keys: string[] = [];
		for (let i = 0; i < sessionStorage.length; i++) {
			const key = sessionStorage.key(i);
			if (key === STORAGE_PREFIX || (key?.startsWith(`${STORAGE_PREFIX}:`) && key !== currentKey)) {
				keys.push(key);
			}
		}
		for (const key of keys) sessionStorage.removeItem(key);
	} catch {
		// Best effort: every supported draft remains scoped by user id.
	}
}

/**
 * samlla#cb22 — a stored draft may only fill a form nobody has touched yet.
 * The draft waits for users/me; on a cold cache the user can start typing
 * first, and restoring over that would silently discard their input.
 * samlla#0xhq — "touched" covers every field the draft restores: journal
 * mode, template look, and each field of the single default metric (type,
 * unit, tracking mode, scale, options…), compared against `defaultMetric`.
 */
export function isPristineTrackerForm(
	form: {
		name: string;
		description: string;
		categoryId: string | null;
		sourceTemplateId?: string;
		isJournalMode?: boolean;
		templateColor?: string;
		templateIcon?: string;
		metrics: Array<object>;
	},
	defaultMetric: object = { name: '' }
): boolean {
	if (
		form.name.trim() !== '' ||
		form.description.trim() !== '' ||
		form.categoryId !== null ||
		form.sourceTemplateId !== undefined ||
		(form.isJournalMode ?? false) ||
		form.templateColor !== undefined ||
		form.templateIcon !== undefined ||
		form.metrics.length !== 1
	) {
		return false;
	}
	const metric = form.metrics[0] as Record<string, unknown>;
	return Object.entries(defaultMetric).every(([key, fallback]) => {
		const value = metric[key];
		if (typeof fallback === 'string') return String(value ?? '').trim() === fallback.trim();
		return JSON.stringify(value) === JSON.stringify(fallback);
	});
}
